import React, { useEffect, useState, useCallback } from "react";
import "./ConversationSidebar.css";

/**
 * ConversationSidebar
 *
 * ChatGPT-style history panel. Calls GET /api/threads (authenticated) and
 * renders threads grouped by: Today / Yesterday / This Week (past 7 days only).
 *
 * Props:
 *   isOpen           {boolean}  — sidebar visibility
 *   onClose          {fn}       — collapse sidebar
 *   activeThreadId   {string}   — currently loaded thread (highlighted)
 *   onSelectThread   {fn(threadId, messages)} — load a past thread
 *   onNewThread      {fn}       — start a fresh conversation
 *   getToken         {fn async} — returns Bearer token string or null
 */
export default function ConversationSidebar({
  isOpen,
  onClose,
  activeThreadId,
  onSelectThread,
  onNewThread,
  getToken
}) {
  const [threads, setThreads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingThread, setLoadingThread] = useState(null);
  const [error, setError] = useState("");
  // Yesterday and This Week start collapsed; Today starts expanded
  const [collapsedBuckets, setCollapsedBuckets] = useState(new Set(["Yesterday", "This Week"]));
  // Tracks which buckets have clicked "X more" to reveal all items
  const [expandedAllBuckets, setExpandedAllBuckets] = useState(new Set());
  const TODAY_PREVIEW = 5;

  const toggleBucket = (label) => {
    setCollapsedBuckets(prev => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label);
      else next.add(label);
      return next;
    });
  };

  const expandAll = (label) => {
    setExpandedAllBuckets(prev => new Set([...prev, label]));
  };

  // ── Resolve API base URL (PPE-aware) ──────────────────────────────────────
  const threadsApiBase =
    window.location.hostname === "localhost"
      ? "/api/threads"
      : `${process.env.REACT_APP_AGENT_API_URL
          ?.replace(/\/agent$/, "/threads") ||
          "https://stratalign-factory-api-ppe.azurewebsites.net/api/threads"}`;

  // ── Fetch thread list ─────────────────────────────────────────────────────
  const fetchThreads = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = await getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(threadsApiBase, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setThreads(data.threads || []);
    } catch (err) {
      setError("Could not load history");
      console.warn("[Sidebar] fetchThreads error:", err.message);
    } finally {
      setLoading(false);
    }
  }, [threadsApiBase, getToken]);

  useEffect(() => {
    if (isOpen) fetchThreads();
  }, [isOpen, fetchThreads]);

  // ── Load a specific thread ────────────────────────────────────────────────
  const handleSelect = async (threadId) => {
    if (threadId === activeThreadId) return;
    setLoadingThread(threadId);
    try {
      const token = await getToken();
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await fetch(`${threadsApiBase}/${threadId}`, { headers });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      onSelectThread(threadId, data.messages || []);
    } catch (err) {
      console.warn("[Sidebar] handleSelect error:", err.message);
      setError("Could not load thread");
    } finally {
      setLoadingThread(null);
    }
  };

  // ── Relative time helper (Today items only) ───────────────────────────────
  const getRelativeTime = (date) => {
    const diffMs = Date.now() - new Date(date).getTime();
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHrs = Math.floor(diffMins / 60);
    return `${diffHrs}h ago`;
  };

  // ── Date bucketing ────────────────────────────────────────────────────────
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfToday.getDate() - 1);
  const start7DaysAgo = new Date(startOfToday);
  start7DaysAgo.setDate(startOfToday.getDate() - 7);

  const buckets = {
    "Today": [],
    "Yesterday": [],
    "This Week": []
  };

  for (const t of threads) {
    const d = new Date(t.lastUpdated);
    if (d >= startOfToday)          buckets["Today"].push(t);
    else if (d >= startOfYesterday) buckets["Yesterday"].push(t);
    else if (d >= start7DaysAgo)    buckets["This Week"].push(t);
    // threads older than 7 days are hidden (backend filters them out)
  }

  return (
    <aside className={`conv-sidebar${isOpen ? ' conv-sidebar--open' : ''}`}>
      {/* Header */}
      <div className="conv-sidebar__header">
        <span className="conv-sidebar__title">History</span>
        <button
          className="conv-sidebar__close"
          onClick={onClose}
          title="Close sidebar"
        >✕</button>
      </div>

      {/* New Conversation button */}
      <button className="conv-sidebar__new-btn" onClick={onNewThread}>
        + New Conversation
      </button>

      {/* Thread list */}
      <div className="conv-sidebar__list">
        {loading && <p className="conv-sidebar__status">Loading…</p>}
        {error  && <p className="conv-sidebar__status conv-sidebar__status--error">{error}</p>}
        {!loading && !error && threads.length === 0 && (
          <p className="conv-sidebar__status">No past conversations yet.</p>
        )}

        {Object.entries(buckets).map(([label, items]) => {
          if (items.length === 0) return null;
          const isCollapsed = collapsedBuckets.has(label);
          const showAll = expandedAllBuckets.has(label);
          const previewCount = label === "Today" ? TODAY_PREVIEW : items.length;
          const visibleItems = (!isCollapsed && !showAll && items.length > previewCount)
            ? items.slice(0, previewCount)
            : items;
          const hiddenCount = items.length - previewCount;
          return (
            <div key={label} className="conv-sidebar__bucket">
              <button
                className="conv-sidebar__bucket-header"
                onClick={() => toggleBucket(label)}
                aria-expanded={!isCollapsed}
              >
                <span className="conv-sidebar__bucket-label">{label}</span>
                <span className={`conv-sidebar__bucket-chevron${isCollapsed ? '' : ' conv-sidebar__bucket-chevron--open'}`}>
                  {isCollapsed ? '›' : '▾'}
                </span>
              </button>
              {!isCollapsed && (
                <div className="conv-sidebar__bucket-items">
                  {visibleItems.map(t => (
                    <button
                      key={t.id}
                      className={`conv-sidebar__thread ${t.threadId === activeThreadId ? "conv-sidebar__thread--active" : ""}`}
                      onClick={() => handleSelect(t.threadId)}
                      disabled={loadingThread === t.threadId}
                      title={t.title}
                    >
                      {loadingThread === t.threadId ? (
                        <span className="conv-sidebar__thread-loading">Loading…</span>
                      ) : (
                        <>
                          <span className="conv-sidebar__thread-title">{t.title || "Untitled"}</span>
                          {t.subtitle && <span className="conv-sidebar__thread-subtitle">{t.subtitle}</span>}
                          {label === "Today" && <span className="conv-sidebar__thread-time">{getRelativeTime(t.lastUpdated)}</span>}
                        </>
                      )}
                    </button>
                  ))}
                  {!showAll && hiddenCount > 0 && (
                    <button className="conv-sidebar__more-btn" onClick={() => expandAll(label)}>
                      {hiddenCount} more ›
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Refresh button */}
      <button
        className="conv-sidebar__refresh"
        onClick={fetchThreads}
        disabled={loading}
      >
        ↻ Refresh
      </button>
    </aside>
  );
}
